import { useCallback, useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import PaperDraftsSheet from '@/components/sheets/PaperDraftsSheet.jsx';
import { useRegenerateBinder } from '@/hooks/useRegenerateBinder.js';
import { getStandardSet, sendStandardSet } from '@/services/documentSends.service.js';
import { DOCUMENTS_CHANGED_EVENT, announceDocumentsChanged } from '@/services/deals.service.js';
import { CcField, CcResult, DocumentLine, MissingItem, ccList, missingWord, sendWord } from './sendSheetParts.jsx';

// "Send the standard set" (paperwork-1 D10, screen 3; L3-2). One line says what it is for; every recipient by name with the
// documents each gets (derived name, the original filename beside it, size), what was already delivered, what is missing with
// the button that fixes it, then one Send. Afterwards it shows what went to whom, read back from document_sends. Who gets what
// is the server's rule (utils/standard-set.js); this sheet derives nothing. Round 2 (David 2026-10-31), as ruled again on
// 2026-11-02: a holder with no email is flagged on its line (an email field, or "<client> forwards it"), and only that choice
// puts its certificate on the client's email to forward (said beside it and in "Goes to the client to forward"); the Send
// button waits and names it until one is made, and the server refuses the same (409 HOLDER_CHOICE_NEEDED); an
// other interest with no certificate is listed with its drop button; "Add others (CC)" copies the client's email (Q21). The
// client's list carries a copy of every holder's document (David 2026-11-02), each line saying whose, and a line under it.

const roleWord = (r) => {
  if (r.kind === 'client') return 'Client';
  if (r.roles?.other) return r.type || 'Interest';
  if (r.roles?.loss_payee && r.roles?.additional_insured) return 'Additional insured and loss payee';
  if (r.roles?.loss_payee) return r.type || 'Loss payee';
  if (r.roles?.additional_insured) return 'Additional insured';
  return r.type || 'Interest';
};

// The client gets a copy of every document going to a holder (David 2026-11-02); the rule marks each copy_of, or forward_to when
// the client is asked to forward it. The sheet only says which.
const attachmentNote = (a, r) => {
  if (a.forward_to) return `For ${a.forward_to.name}: we hold no email for them, so ${r.label || 'the client'} is asked in the email to forward it`;
  if (a.copy_of) return `A copy of what goes to ${a.copy_of.name}, for ${r.label || 'the client'}'s records`;
  return null;
};
const copyHolders = (r) => [...new Set(r.attachments.filter((a) => a.copy_of).map((a) => a.copy_of.name || 'a holder'))];

function Recipient({ r, ctx, reload, onResend }) {
  return (
    <section className="rounded-card border border-border p-3" data-recipient={r.kind === 'client' ? 'client' : r.interest_id}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 className="text-sm font-semibold text-text">{r.label || 'Unnamed'} <span className="font-normal text-text-dim">· {roleWord(r)}</span></h3>
        <span className="text-[12px] text-text-muted">{r.address || 'No email address'}</span>
      </div>
      {r.attachments.length ? (
        <>
          <p className="mt-2 text-[12px] text-text-muted">{r.attachments.length === 1 ? 'Gets this document, attached:' : `Gets these ${r.attachments.length} documents, attached${r.mails > 1 ? ` (in ${r.mails} emails, to stay under the size limit)` : ''}:`}</p>
          <ul className="mt-1 space-y-1.5">{r.attachments.map((a) => <DocumentLine key={a.key} doc={a} note={attachmentNote(a, r)} />)}</ul>
          {copyHolders(r).length ? <p className="mt-1.5 text-[12px] text-text-muted" data-client-copies>{r.label || 'The client'} also gets a copy of what goes to {copyHolders(r).join(', ')}; the email says which attachments are copies.</p> : null}
        </>
      ) : <p className="mt-2 text-[12px] text-text-dim">{r.already_delivered.length && !r.missing.length ? `Everything for ${r.label || 'this recipient'} is already delivered.` : `Nothing to send to ${r.label || 'this recipient'} yet.`}</p>}
      {r.already_delivered.length ? (
        <ul className="mt-2 space-y-1.5">
          {r.already_delivered.map((a) => (
            <div key={a.key}>
              <DocumentLine doc={{ ...a, earlier_sends: [] }} note={`Already delivered, not sent again: ${sendWord(a.sends.find((s) => s.delivered))}`} />
              {r.address ? <Button size="sm" variant="ghost" className="mt-1 h-auto whitespace-normal py-1 text-left text-[12px]" onClick={() => onResend(a.document_id || a.bundle_inbound_document_id)}>Send {a.name} to {r.label} again</Button> : null}
            </div>
          ))}
        </ul>
      ) : null}
      {r.missing.length ? <ul className="mt-2 space-y-1.5">{r.missing.map((m) => <MissingItem key={m.action} item={m} ctx={ctx} onDone={reload} />)}</ul> : null}
    </section>
  );
}

function NotSent({ notSent, forwarded, ctx, reload, clientLabel }) {
  const lines = [
    ...(notSent.orphaned || []).map((i) => `${i.name}: its aircraft is not on this policy, so nothing is sent to it`),
    ...(notSent.voided || []).map((d) => `${d.name}: its transaction was voided, never sent`),
    ...(notSent.declined || []).map((d) => `${d.name}: the carrier declined it, never sent`),
    ...(notSent.superseded || []).map((d) => `${d.name}: replaced by a newer document, never sent`),
    ...(notSent.unmatched_certificates || []).map((d) => `${d.name}: names no one who receives the standard set`),
  ];
  const fwd = forwarded || [];
  const noCert = notSent.other_interests || [];
  if (!lines.length && !fwd.length && !noCert.length) return null;
  return (
    <>
      {fwd.length ? (
        <section className="rounded-card border border-border p-3 text-[12px] text-text-muted" data-forwarded>
          <h3 className="mb-1 text-sm font-semibold text-text">Goes to {clientLabel || 'the client'} to forward</h3>
          <ul className="space-y-1">{fwd.map((i) => (
            <li key={i.interest_id} className="flex flex-wrap items-center justify-between gap-2" data-forwarded-holder={i.interest_id}>
              <span>{i.name} ({i.type || 'interest'}): no email on file{i.chosen ? ', and you chose' : ', so'} its certificate is attached to {clientLabel || 'the client'}'s email with a line asking to forward it</span>
              {i.chosen && ctx.onUnforward ? <Button size="sm" variant="ghost" className="h-auto min-h-8 whitespace-normal" onClick={() => ctx.onUnforward(i.interest_id)} data-unforward-holder={i.interest_id}>Add an email for {i.name} instead</Button> : null}
            </li>
          ))}</ul>
        </section>
      ) : null}
      {noCert.length ? (
        <section className="rounded-card border border-border p-3" data-no-certificate>
          <h3 className="mb-1 text-sm font-semibold text-text">No certificate on file, so nothing goes to them</h3>
          <ul className="space-y-1.5">{noCert.map((i) => <MissingItem key={i.interest_id} item={{ action: `drop_certificate:${i.interest_id}`, reason: 'no_certificate', name: i.name }} ctx={ctx} onDone={reload} />)}</ul>
        </section>
      ) : null}
      {lines.length ? (
        <section className="rounded-card border border-border p-3 text-[12px] text-text-muted" data-not-sent>
          <h3 className="mb-1 text-sm font-semibold text-text">Not sent, and why</h3>
          <ul className="list-disc space-y-0.5 pl-4">{lines.map((l) => <li key={l}>{l}</li>)}</ul>
        </section>
      ) : null}
    </>
  );
}

// The stale binder (David 2026-11-01): the approved NDEMNIFLY binder printed facts that changed since it was rendered (a policy
// number saved, a limit corrected...). The server holds the whole set (409 BINDER_STALE); this says so and names what changed.
// "Regenerate the binder" does what it says (David: "one press regenerates, then opens the review"): it renders a fresh draft
// through the draft door, then opens the paperwork review on it, where Approve & file is still the one file step. While it
// runs it says so; a failed render says why and opens nothing.
function BinderStale({ stale, onRegenerate, regenerating, failure }) {
  return (
    <section className="rounded-card border border-warning/60 bg-warning-soft/30 p-3" data-binder-stale>
      <h3 className="text-sm font-semibold text-text">Binder is out of date: regenerate before sending</h3>
      {stale.changes?.length ? (
        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[12px] text-text-muted">{stale.changes.map((c) => <li key={`${c.fact}-${c.words}`}>{c.words}</li>)}</ul>
      ) : <p className="mt-1 text-[12px] text-text-muted">{stale.words}</p>}
      <p className="mt-1 text-[12px] text-text-muted">Nothing in the standard set goes until a fresh binder is regenerated and approved.</p>
      {failure ? <p className="mt-1 text-[12px] text-warning" data-regenerate-failed>The binder was not regenerated: {failure}</p> : null}
      <Button size="sm" variant="accent" className="mt-2 h-auto min-h-8 whitespace-normal" onClick={onRegenerate} disabled={regenerating} data-regenerate-binder>{regenerating ? 'Regenerating the binder...' : 'Regenerate the binder'}</Button>
    </section>
  );
}

function Result({ result }) {
  return (
    <div className="space-y-3" data-send-result>
      <p className="text-sm text-text">What went to whom (read back from the send record):</p>
      <CcResult out={result} to={(result.sent.find((r) => r.kind === 'client') || {}).label} />
      {result.sent.map((r) => (
        <section key={`${r.kind}-${r.interest_id || 'client'}`} className="rounded-card border border-border p-3">
          <h3 className="text-sm font-semibold text-text">{r.label} <span className="font-normal text-text-dim">· {r.address || 'no email address'}</span></h3>
          {r.mails.map((m) => (
            <div key={m.part} className="mt-1.5">
              {r.mails.length > 1 ? <div className="text-[11px] text-text-dim">Email {m.part} of {r.mails.length}</div> : null}
              {m.error ? <div className="text-[12px] text-warning">This email failed: {m.error}</div> : null}
              <ul className="space-y-0.5">
                {m.documents.map((d) => <li key={d.name} className="text-[12px]"><span className="text-text">{d.name}</span> <span className="text-text-muted">· {sendWord(d.send)}{d.forward_to ? ` · to forward to ${d.forward_to.name}` : d.copy_of ? ` · a copy of what went to ${d.copy_of.name}` : ''}</span></li>)}
              </ul>
            </div>
          ))}
          {!r.mails.length ? <div className="text-[12px] text-text-dim">Nothing was sent{(r.missing || []).length ? `: ${r.missing.map((m) => missingWord(m.reason)).join('; ')}` : ''}.</div> : null}
          {r.mails.length ? (r.missing || []).map((m) => <div key={m.action} className="text-[12px] text-warning">Not sent: {m.name ? `${m.name}: ` : ''}{missingWord(m.reason)}</div>) : null}
          {r.not_sent.map((x) => <div key={x.name} className="text-[12px] text-warning">{x.name}: {x.reason === 'too_large' ? 'too large to attach, not sent' : `could not be read, not sent${x.error ? ` (${x.error})` : ''}`}</div>)}
        </section>
      ))}
    </div>
  );
}

export default function StandardSetSheet({ dealId, policyLabel, open, onOpenChange, onSent }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewDraftId, setReviewDraftId] = useState(null);
  const binder = useRegenerateBinder(dealId);
  // "Send it again" (each document once per address, unless asked): the ids asked, sent with the preview and the send alike.
  const [resend, setResend] = useState([]);
  const [cc, setCc] = useState('');
  // The holders with no email the broker chose "Client forwards it" for (David 2026-11-02): asked with the preview and the send.
  const [forward, setForward] = useState([]);

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true); setError(null);
    try { setPreview(await getStandardSet(dealId, resend, forward)); }
    catch (e) { setPreview(null); setError(e instanceof ApiError ? e.body?.error || e.code : e.message); }
    finally { setLoading(false); }
  }, [dealId, resend, forward]);
  useEffect(() => { if (open) { setResult(null); load(); } }, [open, load]);
  useEffect(() => { if (!open) { setResend([]); setCc(''); setForward([]); } }, [open]);
  useEffect(() => {
    const on = (ev) => { if (open && String(ev?.detail?.dealId) === String(dealId)) load(); };
    window.addEventListener(DOCUMENTS_CHANGED_EVENT, on);
    return () => window.removeEventListener(DOCUMENTS_CHANGED_EVENT, on);
  }, [open, dealId, load]);

  const toSend = preview ? preview.recipients.filter((r) => r.address && r.attachments.length) : [];
  // "Add others (CC)" rides the client's own mail, so it is offered only when the client has something to receive (review fix,
  // round 2): a set that sends him nothing copies nobody.
  const clientGets = toSend.some((r) => r.kind === 'client');
  const docCount = toSend.reduce((n, r) => n + r.attachments.length, 0);

  async function handleSend() {
    if (!preview) return;
    setSending(true);
    try {
      const out = await sendStandardSet(dealId, preview.plan_key, resend, clientGets ? ccList(cc) : [], forward);
      setResult(out);
      const failed = out.sent.flatMap((r) => r.mails).filter((m) => m.error).length;
      if (failed) toast.error(`${failed} email${failed === 1 ? '' : 's'} failed; the list says which`);
      else toast.success(`Sent ${out.send_count} document${out.send_count === 1 ? '' : 's'} to ${out.sent.filter((r) => r.mails.length).length} recipient${out.sent.length === 1 ? '' : 's'}`);
      if (out.delivered_count < out.send_count) toast.info(`${out.send_count - out.delivered_count} of them did not reach the intended address (see each line)`);
      // The surfaces that show what was sent (the interests panel's chips) read again, so none is left stale.
      announceDocumentsChanged(dealId);
      if (typeof onSent === 'function') onSent(out);
    } catch (e) {
      if (e instanceof ApiError && e.body?.code === 'STANDARD_SET_CHANGED' && e.body.preview) {
        setPreview(e.body.preview);
        toast.info('The standard set changed since you opened it. Nothing was sent; check it and press Send again.');
      } else if (e instanceof ApiError && e.body?.code === 'HOLDER_CHOICE_NEEDED' && e.body.preview) {
        setPreview(e.body.preview);
        toast.info(e.body.error);
      } else if (e instanceof ApiError && e.body?.code === 'BINDER_STALE' && e.body.preview) {
        setPreview(e.body.preview);
        toast.info('Binder is out of date: regenerate before sending. Nothing was sent.');
      } else toast.error(`Not sent: ${e instanceof ApiError ? e.body?.error || e.code : e.message}`);
    } finally { setSending(false); }
  }

  // One press: a fresh draft, then the review opened on it; a failure opens nothing (the hook says why).
  const regenerateBinder = async () => {
    const id = await binder.regenerate();
    if (id) { setReviewDraftId(id); setReviewOpen(true); }
  };
  const ctx = { dealId, client: preview?.client, onReviewPaperwork: () => setReviewOpen(true), onRegenerateBinder: regenerateBinder, regeneratingBinder: binder.regenerating,
    onForward: (id) => setForward((x) => (x.includes(String(id)) ? x : [...x, String(id)])), onUnforward: (id) => setForward((x) => x.filter((y) => y !== String(id))) };
  // The holders still waiting for an email or the choice: the Send button names them and waits (the server refuses the same).
  const waiting = preview?.choices_needed || [];
  const label = policyLabel || preview?.policy_label || 'this policy';
  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-2xl" data-standard-set-sheet>
        <SheetHeader>
          <SheetTitle>Send the standard set for {label}</SheetTitle>
          <SheetDescription>Who gets which of this policy's documents, each attached to its own email. Nothing goes until you press Send.</SheetDescription>
        </SheetHeader>
        <div className="-mx-1 mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto px-1 pb-2">
          {loading && !preview ? <p className="text-sm text-text-muted">Reading the policy's documents and recipients...</p> : null}
          {error ? <p className="text-sm text-warning">Could not read the standard set: {error}</p> : null}
          {result ? <Result result={result} /> : null}
          {!result && preview ? (
            <>
              {preview.binder_stale ? <BinderStale stale={preview.binder_stale} onRegenerate={regenerateBinder} regenerating={binder.regenerating} failure={binder.failure} /> : null}
              {preview.recipients.map((r) => <Recipient key={`${r.kind}-${r.interest_id || 'client'}`} r={r} ctx={ctx} reload={load} onResend={(id) => setResend((x) => (x.includes(String(id)) ? x : [...x, String(id)]))} />)}
              <NotSent notSent={preview.not_sent} forwarded={preview.forwarded} ctx={ctx} reload={load} clientLabel={preview.client?.label} />
              {clientGets ? <CcField value={cc} onChange={setCc} to={preview.client?.label || preview.client?.address} disabled={sending} /> : null}
            </>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
          {result ? (
            <>
              {/* A recipient with something not sent for a fixable reason: back to the set, whose missing items carry their buttons. */}
              {result.sent.filter((r) => (r.missing || []).length).map((r) => (
                <Button key={`fix-${r.kind}-${r.interest_id || 'client'}`} variant="ghost" className="h-auto min-h-9 whitespace-normal" onClick={() => { setResult(null); load(); }}>Fix what is missing for {r.label || 'this recipient'}</Button>
              ))}
              <Button variant="outline" onClick={() => onOpenChange?.(false)}>Close</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={sending}>Cancel</Button>
              <Button variant="accent" className="h-auto min-h-9 whitespace-normal" disabled={sending || loading || !preview?.sendable} onClick={handleSend} data-send-standard-set>
                <Send size={14} strokeWidth={2} />
                {sending ? 'Sending...' : preview?.binder_stale ? 'Regenerate the binder first' : waiting.length ? `Add an email or choose forwarding for ${waiting.map((c) => c.name || 'a holder').join(', ')}` : docCount ? `Send ${docCount} document${docCount === 1 ? '' : 's'} to ${toSend.length} recipient${toSend.length === 1 ? '' : 's'}` : 'Nothing to send'}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
    <PaperDraftsSheet dealId={dealId} open={reviewOpen} focusDraftId={reviewDraftId} onOpenChange={(o) => { setReviewOpen(o); if (!o) { setReviewDraftId(null); load(); } }} onChange={load} />
    </>
  );
}
